import { useState, useEffect } from 'react'
import { format, addMonths, subMonths, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isSameMonth, isSameDay, isToday, parseISO } from 'date-fns'
import { zhCN } from 'date-fns/locale'

interface CalendarProps {
  selectedDate: Date | null
  onDateSelect: (date: Date) => void
  appointments?: any[]
  showAppointments?: boolean
}

export default function Calendar({ selectedDate, onDateSelect, appointments = [], showAppointments = false }: CalendarProps) {
  const [currentMonth, setCurrentMonth] = useState(new Date())

  const monthStart = startOfMonth(currentMonth)
  const monthEnd = endOfMonth(monthStart)
  const startDate = startOfWeek(monthStart, { locale: zhCN })
  const endDate = endOfWeek(monthEnd, { locale: zhCN })

  const days = eachDayOfInterval({ start: startDate, end: endDate })

  const prevMonth = () => setCurrentMonth(subMonths(currentMonth, 1))
  const nextMonth = () => setCurrentMonth(addMonths(currentMonth, 1))
  const goToToday = () => {
    const today = new Date()
    setCurrentMonth(today)
    onDateSelect(today)
  }

  // 当外部 selectedDate 变化时，如果在不同月份，切换月份
  useEffect(() => {
    if (selectedDate && !isSameMonth(selectedDate, currentMonth)) {
      setCurrentMonth(selectedDate)
    }
  }, [selectedDate])

  const getAppointmentsForDay = (date: Date) => {
    if (!appointments || !date) return []
    return appointments.filter(apt => {
      if (!apt.appointment_date) return false
      // 标准化日期比较
      const aptDate = parseISO(apt.appointment_date + 'T00:00:00')
      return isSameDay(aptDate, date)
    })
  }

  return (
    <div className="card p-4">
      {/* 头部 */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={prevMonth}
          className="p-2 hover:bg-primary-50 rounded-lg transition-colors"
          type="button"
        >
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        
        <div className="text-center">
          <h3 className="text-lg font-semibold text-gray-900">
            {format(currentMonth, 'yyyy 年 M 月', { locale: zhCN })}
          </h3>
          <button
            onClick={goToToday}
            className="text-xs text-primary-600 hover:text-primary-700"
            type="button"
          >
            回到今天
          </button>
        </div>
        
        <button
          onClick={nextMonth}
          className="p-2 hover:bg-primary-50 rounded-lg transition-colors"
          type="button"
        >
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* 星期标题 */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {['日', '一', '二', '三', '四', '五', '六'].map(day => (
          <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
            {day}
          </div>
        ))}
      </div>

      {/* 日期网格 */}
      <div className="grid grid-cols-7 gap-1">
        {days.map((day) => {
          const dayAppointments = getAppointmentsForDay(day)
          const hasAppointments = dayAppointments.length > 0
          const isSelected = selectedDate && isSameDay(day, selectedDate)
          const isCurrentMonth = isSameMonth(day, currentMonth)
          
          return (
            <button
              key={day.toISOString()}
              onClick={() => {
                // 确保传递的日期是当天的开始时间
                const selectedDay = new Date(day.getFullYear(), day.getMonth(), day.getDate())
                onDateSelect(selectedDay)
              }}
              type="button"
              className={`
                relative p-2 rounded-lg transition-all min-h-[60px]
                ${!isCurrentMonth ? 'text-gray-300 bg-gray-50' : ''}
                ${isToday(day) ? 'bg-primary-100 font-semibold' : ''}
                ${isSelected ? 'ring-2 ring-primary-500 bg-primary-50' : 'hover:bg-primary-50'}
                ${hasAppointments && isCurrentMonth ? 'bg-green-50' : ''}
              `}
            >
              <span className={`
                text-sm
                ${isToday(day) ? 'text-primary-600' : ''}
                ${isSelected ? 'font-bold' : ''}
              `}>
                {format(day, 'd')}
              </span>
              
              {hasAppointments && showAppointments && isCurrentMonth && (
                <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-0.5">
                  {dayAppointments.slice(0, 3).map((_, i) => (
                    <div key={i} className="w-1.5 h-1.5 rounded-full bg-green-500" />
                  ))}
                  {dayAppointments.length > 3 && (
                    <span className="text-[8px] text-green-600 leading-none">+{dayAppointments.length - 3}</span>
                  )}
                </div>
              )}
            </button>
          )
        })}
      </div>

      {/* 图例 */}
      {showAppointments && (
        <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-center gap-4 text-xs text-gray-500">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-green-500" />
            <span>有预约</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-primary-100" />
            <span>今天</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded ring-2 ring-primary-500" />
            <span>已选择</span>
          </div>
        </div>
      )}
    </div>
  )
}
